import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import PayButton from "../components/PayButton";
import SetupBadge from "../components/SetupBadge";
import { Crown, Check, Sparkles, Building2, AlertCircle, X, RefreshCw } from "lucide-react";
import { track } from "../lib/analytics";

// Load Razorpay Checkout SDK once on first need (Razorpay subscription mode).
const ensureRzpScript = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve();
  s.onerror = () => reject(new Error("Could not load Razorpay Checkout"));
  document.body.appendChild(s);
});

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState({ parent: null, vendor: null });
  const [side, setSide] = useState("parent");
  const [msg, setMsg] = useState("");

  const isVendor = ["vet", "store_owner", "hostel_owner", "ngo"].includes(user?.role);

  const load = async () => {
    const [pr, mr] = await Promise.all([
      axios.get(`${API}/billing/plans`),
      axios.get(`${API}/billing/my-subscription`),
    ]);
    setPlans(pr.data.plans);
    setMySub(mr.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (isVendor) setSide("vendor"); }, [isVendor]);

  const activate = async (plan, payment_intent_id) => {
    try {
      await axios.post(`${API}/billing/subscribe`, { plan_id: plan.plan_id, payment_intent_id });
      setMsg(`${plan.name} is now active 🎉`);
      load();
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not activate plan");
    }
  };

  const startTrial = async (plan) => {
    try {
      await axios.post(`${API}/billing/subscribe`, { plan_id: plan.plan_id, trial: true });
      setMsg(`🎁 Your ${plan.trial_days}-day free trial of ${plan.name} has started!`);
      load();
      setTimeout(() => setMsg(""), 5000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not start trial");
    }
  };

  const cancel = async (s) => {
    if (!confirm("Cancel your subscription? You'll lose access at the end of the period.")) return;
    await axios.post(`${API}/billing/cancel?side=${s}`);
    load();
  };

  const sidePlans = plans.filter(p => p.side === side);
  const current = side === "parent" ? mySub.parent : mySub.vendor;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <header>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2"><Crown className="w-7 h-7 text-brand"/> Plans &amp; Billing</h1>
          <SetupBadge type="payments" country="IN" showWhenConfigured={true}/>
        </div>
        <p className="text-ink-soft text-sm">Pick the plan that fits you. Cancel anytime. Powered by your active payment gateway.</p>
      </header>

      {isVendor && (
        <div className="inline-flex bg-surface border border-line rounded-full p-1 shadow-soft">
          <button data-testid="side-parent" onClick={() => setSide("parent")} className={`px-4 py-1.5 rounded-full text-sm font-bold ${side === "parent" ? "bg-ink text-white" : "text-ink-soft"}`}>For me (pet parent)</button>
          <button data-testid="side-vendor" onClick={() => setSide("vendor")} className={`px-4 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1 ${side === "vendor" ? "bg-ink text-white" : "text-ink-soft"}`}><Building2 className="w-3.5 h-3.5"/> For my business</button>
        </div>
      )}

      {current ? <CurrentPlanCard sub={current} onCancel={() => cancel(side)}/> : <NoActivePlanCard side={side}/>}

      {msg && <div className="bg-leaf/30 border border-leaf rounded-2xl px-4 py-3 text-sm font-bold inline-flex items-center gap-2"><Check className="w-4 h-4"/> {msg}</div>}

      <section className="grid md:grid-cols-3 gap-4">
        {sidePlans.map(plan => (
          <PlanCard
            key={plan.plan_id}
            plan={plan}
            isCurrent={current?.plan_id === plan.plan_id}
            currentIsTrial={current?.is_trial && current?.plan_id === plan.plan_id}
            onActivate={(intent_id) => activate(plan, intent_id)}
            onStartTrial={() => startTrial(plan)}
          />
        ))}
      </section>

      <footer className="bg-surface-muted rounded-2xl p-4 text-xs text-ink-soft flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
        <span>When a paid plan expires, you'll be downgraded to the Free tier and shown a renewal banner across the app. Cancelled plans run until their expiry date. Refunds for unused time are handled manually by support — contact us if you cancelled by mistake.</span>
      </footer>
    </div>
  );
}

function CurrentPlanCard({ sub, onCancel }) {
  const daysLeft = sub.expires_at ? Math.max(0, Math.ceil((new Date(sub.expires_at) - new Date()) / 86400000)) : null;
  const expiringSoon = daysLeft !== null && daysLeft <= 7;
  const isTrial = sub.is_trial || sub.status === "trialing";
  return (
    <div className={`rounded-3xl p-5 shadow-soft border ${expiringSoon ? "border-brand bg-brand/5" : "border-line bg-surface"}`} data-testid="current-plan-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-ink-soft">Your current plan</div>
          <div className="font-heading text-2xl font-extrabold flex items-center gap-2 mt-0.5">
            <Crown className="w-5 h-5 text-brand"/> {sub.plan_name}
            {isTrial && <span className="bg-leaf text-ink text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid="trial-badge">Free trial</span>}
          </div>
          <div className="text-sm text-ink-soft mt-1">
            {isTrial ? "Trial · " : sub.amount ? `₹${sub.amount} · ` : "Free · "}
            {sub.expires_at ? <>{isTrial ? "Trial ends" : "Renews / expires"} <strong>{new Date(sub.expires_at).toLocaleDateString()}</strong></> : <>No expiry</>}
            {daysLeft !== null && <> · <strong className={expiringSoon ? "text-brand" : ""}>{daysLeft} day{daysLeft === 1 ? "" : "s"} left</strong></>}
          </div>
        </div>
        {(sub.amount > 0 || isTrial) && (
          <button data-testid="cancel-sub" onClick={onCancel} className="text-sm font-bold text-danger inline-flex items-center gap-1 hover:underline"><X className="w-4 h-4"/> Cancel</button>
        )}
      </div>
      {expiringSoon && (
        <div className="mt-3 text-sm bg-sunshine/30 rounded-2xl px-3 py-2 font-bold">
          {isTrial ? "Your trial ends soon — subscribe below to keep Premium." : "Renew below to avoid losing premium features."}
        </div>
      )}
    </div>
  );
}

function NoActivePlanCard({ side }) {
  return (
    <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="no-plan-card">
      <div className="text-xs uppercase tracking-wider font-bold text-ink-soft">Your current plan</div>
      <div className="font-heading text-xl font-extrabold mt-1">{side === "parent" ? "Free" : "Starter (Free)"}</div>
      <p className="text-sm text-ink-soft mt-1">Upgrade below to unlock {side === "parent" ? "24/7 AI vet, unlimited prescriptions and priority booking" : "featured placement, unlimited bookings and advanced analytics"}.</p>
    </div>
  );
}

function PlanCard({ plan, isCurrent, currentIsTrial, onActivate, onStartTrial }) {
  const featured = plan.plan_id.includes("yearly");
  const hasTrial = (plan.trial_days || 0) > 0;
  const hasAutoRenew = !!plan.razorpay_plan_id;
  const [trialEligible, setTrialEligible] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!hasTrial || isCurrent) { setTrialEligible(false); return; }
    axios.get(`${API}/billing/trial-eligibility/${plan.plan_id}`).then(r => setTrialEligible(r.data.eligible)).catch(() => setTrialEligible(false));
  }, [plan.plan_id, hasTrial, isCurrent]);

  const startAutoRenew = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/billing/subscribe-recurring`, { plan_id: plan.plan_id });
      track("subscription_started", { plan_id: plan.plan_id, plan_name: data.plan_name });
      await ensureRzpScript();
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        subscription_id: data.razorpay_subscription_id,
        name: "PetBond",
        description: `${data.plan_name} — auto-renew`,
        prefill: { email: data.user?.email || "", name: data.user?.name || "" },
        theme: { color: "#FF6B35" },
        handler: () => {
          // The webhook does the actual activation; just nudge the parent to refresh.
          setBusy(false);
          onActivate("__autorenew__");
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (resp) => {
        setBusy(false);
        setErr(resp.error?.description || "Mandate registration failed");
      });
      rzp.open();
    } catch (e) {
      setBusy(false);
      setErr(e?.response?.data?.detail || "Could not start auto-renew");
    }
  };

  return (
    <article className={`rounded-3xl p-5 shadow-soft border-2 relative ${isCurrent ? "border-brand bg-brand/5" : featured ? "border-ink bg-surface" : "border-line bg-surface"}`} data-testid={`plan-${plan.plan_id}`}>
      {featured && <div className="absolute -top-3 left-5 bg-ink text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">Best value</div>}
      {hasTrial && !featured && !isCurrent && <div className="absolute -top-3 left-5 bg-brand text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">{plan.trial_days}-day free trial</div>}
      <div className="font-heading text-xl font-extrabold">{plan.name}</div>
      <div className="mt-1 font-heading text-3xl font-extrabold">
        {plan.amount ? `₹${plan.amount}` : "Free"}
        {plan.interval_days > 0 && <span className="text-sm text-ink-soft font-bold ml-1">/ {plan.interval_days === 30 ? "month" : plan.interval_days === 365 ? "year" : `${plan.interval_days} days`}</span>}
      </div>
      {hasAutoRenew && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-sky/15 text-sky px-2 py-0.5 rounded-full">
          <RefreshCw className="w-2.5 h-2.5"/> Auto-renews via UPI / card
        </div>
      )}
      <ul className="mt-4 space-y-1.5 text-sm">
        {(plan.features || []).map(f => (
          <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-leaf shrink-0 mt-0.5"/> <span>{f}</span></li>
        ))}
      </ul>
      <div className="mt-5 space-y-2">
        {isCurrent ? (
          <button disabled className="w-full bg-line text-ink-soft rounded-full py-3 font-bold cursor-default" data-testid={`current-${plan.plan_id}`}>
            {currentIsTrial ? "Active — Trial" : "Current plan"}
          </button>
        ) : plan.amount === 0 ? (
          <button data-testid={`activate-${plan.plan_id}`} onClick={() => onActivate(null)} className="w-full bg-ink text-white rounded-full py-3 font-bold hover:opacity-90">Switch to Free</button>
        ) : (
          <>
            {trialEligible && (
              <button data-testid={`trial-${plan.plan_id}`} onClick={onStartTrial} className="w-full bg-leaf text-ink rounded-full py-3 font-bold hover:opacity-90">
                Start {plan.trial_days}-day free trial
              </button>
            )}
            {hasAutoRenew ? (
              <button
                data-testid={`autorenew-${plan.plan_id}`}
                onClick={startAutoRenew}
                disabled={busy}
                className="w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
              >
                <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`}/>
                {busy ? "Opening Razorpay…" : `Subscribe (auto-renew) · ₹${plan.amount}`}
              </button>
            ) : (
              <PayButton
                amount={plan.amount}
                currency={plan.currency}
                country={plan.currency === "INR" ? "IN" : "US"}
                purpose="subscription"
                referenceId={plan.plan_id}
                label={trialEligible ? `Skip trial · Subscribe ₹${plan.amount}` : `Subscribe · ₹${plan.amount}`}
                onSuccess={(intent) => onActivate(intent.intent_id)}
                dataTestid={`pay-${plan.plan_id}`}
              />
            )}
            {err && <div className="text-xs text-danger bg-danger/10 rounded-2xl px-3 py-2 mt-1" data-testid={`autorenew-err-${plan.plan_id}`}>{err}</div>}
          </>
        )}
      </div>
    </article>
  );
}
